'use client'
import {
    LayoutBottomPaneState,
    LayoutSidebarState,
    LayoutState,
    LayoutTopPaneState,
} from '../components/organisms/Layout/Types/layoutTypes'
import React, { Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react'
import { ReactElement, createContext } from 'react'

export interface LayoutContextProps {
    topPaneState: LayoutTopPaneState
    sidebarState: LayoutSidebarState
    bottomPaneState: LayoutBottomPaneState
    layoutState: LayoutState
    setTopPaneState: (topPaneState: LayoutTopPaneState) => void
    setSidebarState: (sidebarState: LayoutSidebarState) => void
    setBottomPaneState: (bottomPaneState: LayoutBottomPaneState) => void
    setLayoutState: Dispatch<SetStateAction<LayoutState>>
    cleanUpContext: (layoutChildren?: ReactElement[]) => void
}

export const LayoutContext = createContext<LayoutContextProps>({} as LayoutContextProps)

export const useLayoutContext = () => useContext(LayoutContext)

interface LayoutProviderProps {
    children: ReactNode
}

/**
 * @deprecated Prefer not to use the Layout provider. It is not compatible with RSC components.
 * @description
 * The LayoutDelegator provides children through the layoutPanes property in the layoutState.
 * The Layout listens to this context and combines it with it's own state, giving priority to the LayoutProvider.context
 *
 * The LayoutProvider also stores the state of the panes. For example:
 * 1. The LayoutSidebarPane is closed from the LayoutTopPane
 * 2. The LayoutSidebarPane listening to the provider context then closes
 * 3. The LayoutContentPane listening to the provider context, adjusts it's padding and spacing if necessary
 *
 * @TODO make Layout independent of LayoutProvider so it can be used without it when no LayoutDelegator is used.
 * @TODO Make sure that the LayoutPanes don't use the LayoutProvider. The best way is using a StaticStateOfLayoutPanes object.
 * @see {@link https://jira.hosted-tools.com/browse/STOPEN-5761}
 * */
export const LayoutProvider = ({ children }: LayoutProviderProps): JSX.Element => {
    const [topPaneState, setTopPaneState] = useState<LayoutTopPaneState>({})
    const [sidebarState, setSidebarState] = useState<LayoutSidebarState>({})
    const [bottomPaneState, setBottomPaneState] = useState<LayoutBottomPaneState>({})
    const [layoutState, setLayoutState] = useState<LayoutState>({})

    /**
     * Checks whether the layout should have its different types of panes, and if not empties them.
     *
     * @param layoutChildren
     */
    const cleanUpContext = (layoutChildren?: ReactElement[]) => {
        let [hasSidebar, hasTopPane, hasBottomPane] = [false, false, false]

        if (!layoutChildren) {
            setLayoutState({})
        }

        layoutChildren?.forEach((child) => {
            if (child.props.paneType?.toLowerCase().includes('top-pane')) hasTopPane = true
            if (child.props.paneType?.toLowerCase().includes('sidebar')) hasSidebar = true
            if (child.props.paneType?.toLowerCase().includes('bottom-pane')) hasBottomPane = true
        })

        if (!hasTopPane) setTopPaneState({})
        if (!hasSidebar) setSidebarState({})
        if (!hasBottomPane) setBottomPaneState({})
    }

    const value: LayoutContextProps = {
        topPaneState,
        sidebarState,
        bottomPaneState,
        layoutState,
        setTopPaneState,
        setSidebarState,
        setBottomPaneState,
        setLayoutState,
        cleanUpContext,
    }

    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
}
